// Device Characterization and T1 (Readout) Simulation plots, drawn like the desktop's (qdtransport.gui.figures):
// fit vs measured data with a residuals strip; p_es vs load time; Γ_eff vs field.
import { SimpleGrid, Text } from "@mantine/core";
import { useMemo } from "react";
import type { CharDataset } from "../lib/device";
import { PALETTE, Plot } from "./Plot";

type Num = number | null;
const GRID = { gridcolor: "#e6e6e6", griddash: "dash" };

/** Runs of consecutive true values: the desktop never draws a line across excluded points. */
export function runs(mask: boolean[]): number[][] {
  const out: number[][] = [];
  let cur: number[] = [];
  mask.forEach((m, i) => {
    if (m) cur.push(i);
    else if (cur.length) { out.push(cur); cur = []; }
  });
  if (cur.length) out.push(cur);
  return out;
}

const pick = (a: Num[], idx: number[]) => idx.map((i) => a[i]);

export interface FitDataset {
  name: string; label: string; b: Num[]; data: Num[]; model: Num[]; residual: Num[]; included: boolean[];
  curves: { b: Num[]; e: Num[] }[];
}
export interface FitResultJson {
  orientation_label: string; params: Record<string, Num>; labels: Record<string, string>; rms_ueV: Num; rmspe: Num;
  datasets: FitDataset[]; success: boolean; message: string; algorithm_label: string; n_iter: number;
}

const g4 = (v: Num | undefined) => (v == null ? "–" : Number(v.toPrecision(4)).toString());

export function fitSummary(r: FitResultJson): string {
  const box = Object.keys(r.labels).map((k) => `${r.labels[k]} = ${g4(r.params[k])}`).join("  ");
  return `${box}     rms = ${Number((r.rms_ueV ?? 0).toPrecision(3))} µeV, rmspe = ${Number((r.rmspe ?? 0).toPrecision(3))}%`;
}

export function fitFigure(r: FitResultJson) {
  const traces: any[] = [];
  r.datasets.forEach((d, i) => {
    const color = PALETTE[i % PALETTE.length];
    const inc = d.included.map((v, j) => (v ? j : -1)).filter((j) => j >= 0);
    traces.push({ x: pick(d.b, inc), y: pick(d.data, inc), type: "scatter", mode: "markers", name: `${d.name} - ${d.label}`,
      legendgroup: `d${i}`, marker: { color, size: 6 } });
    d.curves.forEach((c) => traces.push({ x: c.b, y: c.e, type: "scatter", mode: "lines", legendgroup: `d${i}`, showlegend: false,
      line: { color, width: 2 }, hoverinfo: "skip" }));
    runs(d.included).forEach((g) => traces.push({ x: pick(d.b, g), y: pick(d.residual, g), type: "scatter", mode: "lines+markers",
      xaxis: "x", yaxis: "y2", legendgroup: `d${i}`, showlegend: false, line: { color, width: 1 }, marker: { color, size: 4 } }));
  });
  const layout = {
    title: { text: `Fit vs. measured data (${r.orientation_label})`, font: { size: 14 } },
    xaxis: { title: { text: "B (T)" }, anchor: "y2", ...GRID },
    yaxis: { title: { text: "Energy (µeV)" }, domain: [0.32, 1], ...GRID },
    yaxis2: { title: { text: "model − data", font: { size: 11 } }, domain: [0, 0.25], zeroline: true, zerolinecolor: "#888", ...GRID },
    legend: { x: 0.01, y: 0.99, bgcolor: "rgba(255,255,255,0.7)", font: { size: 11 } },
    margin: { l: 70, r: 20, t: 45, b: 55 },          // the parameter line (fitSummary) is shown above the plot
  };
  return { traces, layout };
}

/** The uploaded bands as the fit reads them (before a fit): used points solid, points outside the |B| ranges faint. */
export function dataFigure(sets: CharDataset[]) {
  const traces: any[] = [];
  sets.forEach((d, i) => {
    const color = PALETTE[i % PALETTE.length];
    const inc = d.included.map((v, j) => (v ? j : -1)).filter((j) => j >= 0);
    const exc = d.included.map((v, j) => (v ? -1 : j)).filter((j) => j >= 0);
    traces.push({ x: pick(d.b, inc), y: pick(d.e, inc), type: "scatter", mode: "markers", name: `${d.name} - ${d.label}`,
      legendgroup: `d${i}`, marker: { color, size: 6 } });
    if (exc.length) traces.push({ x: pick(d.b, exc), y: pick(d.e, exc), type: "scatter", mode: "markers", legendgroup: `d${i}`,
      name: `${d.name} (not used)`, showlegend: false, marker: { color, size: 6, opacity: 0.25, symbol: "circle-open" } });
  });
  return {
    traces,
    layout: {
      title: { text: "Measured bands (as the fit reads them)", font: { size: 14 } },
      xaxis: { title: { text: "B (T)" }, ...GRID }, yaxis: { title: { text: "Energy (µeV)" }, ...GRID },
      legend: { x: 0.01, y: 0.99, bgcolor: "rgba(255,255,255,0.7)", font: { size: 11 } },
    },
  };
}

export function FitPlots({ result }: { result: FitResultJson }) {
  const fig = useMemo(() => fitFigure(result), [result]);
  return <Plot data={fig.traces} layout={fig.layout} height={520} />;
}

// -- T1 (Readout) Simulation ---------------------------------------------------------------------------------
interface Curve { b: number; load_times: Num[]; p_all: Num[]; p_valid: Num[] }
export interface T1ResultJson {
  protocol: string; bt1: Num; region_note: string; params: Record<string, any>;
  curve_1: Curve; curve_3: Curve; gamma_eff: { b_values: Num[]; protocols: Record<string, Num[]> };
}

const positive = (xs: Num[], ys: Num[]) => {
  const idx = ys.map((y, i) => (y != null && y > 0 && xs[i] != null ? i : -1)).filter((i) => i >= 0);
  return { x: pick(xs, idx), y: pick(ys, idx) };
};

export function loadTimeFigure(r: T1ResultJson) {
  const traces: any[] = [];
  ([["Region I", r.curve_1, PALETTE[0]], ["Region III", r.curve_3, PALETTE[1]]] as const).forEach(([name, c, color]) => {
    const v = positive(c.load_times, c.p_valid);
    if (v.x.length) traces.push({ ...v, type: "scatter", mode: "lines+markers", name: `${name}  (B = ${g4(c.b)} T)`, line: { color } });
    const a = positive(c.load_times, c.p_all);
    if (a.x.length) traces.push({ ...a, type: "scatter", mode: "lines", name: `${name} (all runs)`,
      line: { color, dash: "dot", width: 1 }, opacity: 0.6 });
  });
  return {
    traces,
    layout: {
      title: { text: `Readout blip probability vs load time - protocol ${r.protocol}`, font: { size: 14 } },
      xaxis: { title: { text: "Load time (s)" }, ...GRID },
      yaxis: { title: { text: "p<sub>es</sub> (probability of excited state)" }, type: "log", ...GRID },
      legend: { x: 0.99, xanchor: "right", y: 0.99, bgcolor: "rgba(255,255,255,0.7)", font: { size: 11 } },
      margin: { l: 70, r: 20, t: 45, b: 55 },
    },
  };
}

export function gammaEffFigure(r: T1ResultJson) {
  const b = r.gamma_eff.b_values;
  const traces: any[] = Object.entries(r.gamma_eff.protocols).map(([key, rate], i) => ({
    ...positive(b, rate), type: "scatter", mode: "lines+markers", name: key, line: { color: PALETTE[i % PALETTE.length] }, marker: { size: 5 },
  }));
  const p = r.params;
  const refs: [string, number][] = [["γ<sub>s</sub>", p.gamma_s], ["γ<sub>v</sub>", p.gamma_v], ["γ<sub>k</sub>", p.gamma_k],
    ["γ<sub>s</sub> + γ<sub>v</sub>", p.gamma_s + p.gamma_v]];
  const shapes: any[] = [];
  const annotations: any[] = [];
  for (const [name, value] of refs) {
    if (!(value > 0)) continue;
    shapes.push({ type: "line", xref: "paper", x0: 0, x1: 1, y0: value, y1: value, line: { color: "#888", dash: "dash", width: 1 } });
    annotations.push({ xref: "paper", x: 1, y: Math.log10(value), xanchor: "left", text: name, showarrow: false, font: { size: 11, color: "#555" } });
  }
  if (r.bt1 != null) {
    shapes.push({ type: "line", yref: "paper", x0: r.bt1, x1: r.bt1, y0: 0, y1: 1, line: { color: "#555", dash: "dot", width: 1 } });
    traces.push({ x: [null], y: [null], type: "scatter", mode: "lines", name: "B<sub>t1</sub> (anti-crossing)", line: { color: "#555", dash: "dot", width: 1 } });
  }
  return {
    traces,
    layout: {
      title: { text: `Effective relaxation rate vs field - protocol(s) ${Object.keys(r.gamma_eff.protocols).join(", ")}`, font: { size: 14 } },
      xaxis: { title: { text: "Out-of-plane field B<sub>z</sub> (T)" }, ...GRID },
      yaxis: { title: { text: "Γ<sub>eff</sub> (Hz)" }, type: "log", showgrid: false },
      legend: { x: 0.01, y: 0.99, bgcolor: "rgba(255,255,255,0.7)", font: { size: 11 } },
      shapes, annotations, margin: { l: 70, r: 90, t: 45, b: 55 },
    },
  };
}

export function t1Summary(r: T1ResultJson): string {
  const bt1 = r.bt1 != null ? `${g4(r.bt1)} T` : "none (gv = 0)";
  return `Protocol ${r.protocol}   Bt1 (anti-crossing) = ${bt1}   b1 = ${g4(r.curve_1.b)} T, b3 = ${g4(r.curve_3.b)} T`;
}

export function ReadoutPlots({ result }: { result: T1ResultJson }) {
  const load = useMemo(() => loadTimeFigure(result), [result]);
  const gamma = useMemo(() => gammaEffFigure(result), [result]);
  return (
    <>
      <Text size="sm">{t1Summary(result)}</Text>
      {result.region_note && <Text size="xs" c="dimmed">{result.region_note}</Text>}
      <SimpleGrid cols={{ base: 1, xl: 2 }}>
        <Plot data={load.traces} layout={load.layout} height={440} />
        <Plot data={gamma.traces} layout={gamma.layout} height={440} />
      </SimpleGrid>
    </>
  );
}
